import { useState, useEffect, useRef } from 'react';
import { portalApi } from '../../api';

// 门户「购买开通」：选套餐 → 下单 → 扫码支付（或免费/后台确认收款）→ 轮询取码 → 展示登录信息
export default function PortalBuy() {
  const [plans, setPlans] = useState<any[]>([]);
  const [wxpayEnabled, setWxpayEnabled] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [contact, setContact] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const [qr, setQr] = useState<string>(''); // 支付二维码 dataURL
  const [orderNo, setOrderNo] = useState(''); // 当前订单号
  const [freeIssued, setFreeIssued] = useState<any>(null); // 免费方案直接发码结果
  const [manualTip, setManualTip] = useState(false); // 微信未配置：提示后台确认收款
  const [result, setResult] = useState<any>(null); // 付款成功后的登录信息
  const [copied, setCopied] = useState(false);
  const timer = useRef<any>(null);

  useEffect(() => {
    portalApi('/plans').then((d: any) => {
      setPlans(d.plans || []);
      setWxpayEnabled(!!d.wxpayEnabled);
    }).catch(() => {});
    return () => { if (timer.current) clearInterval(timer.current); };
  }, []);

  // 轮询订单状态，付款成功后取码
  useEffect(() => {
    if (!orderNo || result || freeIssued) return;
    timer.current = setInterval(async () => {
      try {
        const d: any = await portalApi(`/order/status?order_no=${orderNo}`);
        if (d.paid && d.issued) {
          clearInterval(timer.current);
          setResult(d.issued);
        }
      } catch {}
    }, 2000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [orderNo, result, freeIssued]);

  const submit = async (e: any, plan: any) => {
    e.preventDefault();
    setErr('');
    if (!name.trim()) return setErr('请填写公司 / 客户名称');
    if (phone.trim() && !/^1\d{10}$/.test(phone.trim())) return setErr('手机号格式不正确');
    setBusy(true);
    try {
      const d: any = await portalApi('/order/create', {
        method: 'POST',
        body: JSON.stringify({ plan_id: plan.id, name: name.trim(), phone: phone.trim(), contact: contact.trim() }),
      });
      if (d.free && d.issued) {
        setFreeIssued(d.issued);
      } else if (d.wxpayEnabled && d.qr) {
        setQr(d.qr); // 后端已转成可直接展示的二维码 dataURL
        setOrderNo(d.order_no);
        setManualTip(false);
      } else {
        // 微信支付未配置：保留订单，提示联系客服/后台确认收款后自动开通
        setOrderNo(d.order_no);
        setManualTip(true);
      }
    } catch (e: any) {
      setErr(e.message || '下单失败，请重试');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (info: any) => {
    const text = `荣牧科技 ERP 登录信息\n客户编码：${info.code}\n专属密码：${info.password}\n登录网址：https://erp.rongmukj.com/portal`;
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  // 成功页（付款成功 / 免费发码）
  const done = result || freeIssued;
  if (done) {
    return (
      <div className="bg-white rounded-2xl shadow p-6 space-y-4">
        <div className="text-center">
          <div className="text-3xl mb-1">🎉</div>
          <h1 className="text-xl font-bold">{freeIssued ? '开通成功' : '支付成功，已自动开通'}</h1>
          <p className="text-xs text-red-500 mt-1">请立即保存，密码只显示这一次</p>
        </div>
        <div className="bg-slate-50 rounded-xl divide-y divide-slate-200 text-sm">
          <div className="flex justify-between px-4 py-3"><span className="text-slate-400">客户编码</span><span className="font-bold">{done.code}</span></div>
          {done.password && (<div className="flex justify-between px-4 py-3"><span className="text-slate-400">专属密码</span><span className="font-bold font-mono text-red-600 tracking-wide">{done.password}</span></div>)}
          {done.plan && (<div className="flex justify-between px-4 py-3"><span className="text-slate-400">套餐</span><span className="font-medium">{done.plan}</span></div>)}
          {done.license_expire_at && (<div className="flex justify-between px-4 py-3"><span className="text-slate-400">有效期至</span><span className="font-medium">{done.license_expire_at}</span></div>)}
          {done.renewed && (<div className="px-4 py-3 text-emerald-600">✅ 已为已有账号续费，有效期已延长</div>)}
        </div>
        {done.password && (<button onClick={() => copy(done)} className="w-full bg-emerald-600 text-white rounded-lg py-3 text-base font-bold hover:bg-emerald-700 transition">{copied ? '已复制 ✓' : '复制登录信息'}</button>)}
        <button onClick={() => window.location.href = '/portal/login'} className="w-full bg-emerald-50 text-emerald-700 rounded-lg py-3 text-base font-bold hover:bg-emerald-100 transition">去登录</button>
      </div>
    );
  }

  // 等待支付 / 后台确认收款
  if (qr || manualTip) {
    return (
      <div className="bg-white rounded-2xl shadow p-6 space-y-4 text-center">
        <h1 className="text-lg font-bold">
          {manualTip ? '订单已提交，等待确认收款' : '请使用微信扫码支付'}
        </h1>
        {qr && (
          <div className="flex flex-col items-center space-y-2">
            <img src={qr} alt="pay-qr" className="w-56 h-56 border rounded-lg" />
            <p className="text-xs text-slate-400">打开微信 → 扫一扫 → 完成支付，本页自动跳转</p>
          </div>
        )}
        {manualTip && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700 space-y-1">
            <p>当前微信支付尚未配置，订单号 <b>{orderNo}</b> 已生成。</p>
            <p>请将款项转给荣牧科技业务员，<b>后台「客户管理 → 购买订单」点"确认收款"</b>后，系统会<b>自动生成激活码并开通账号</b>，您会在此页直接看到登录信息。</p>
          </div>
        )}
        <p className="text-xs text-slate-400">订单号：{orderNo}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h1 className="text-lg font-bold">购买 ERP 授权</h1>
        <p className="text-xs text-slate-400 mt-1">付款后自动开通，无需等待人工</p>
      </div>

      <div className="bg-white rounded-2xl shadow p-4 space-y-3">
        <input className="w-full border border-slate-200 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="公司 / 客户名称 *" value={name} onChange={(e) => setName(e.target.value)} />
        <input className="w-full border border-slate-200 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="手机号（选填，用于接收短信）" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <input className="w-full border border-slate-200 rounded-lg px-4 py-3 text-base focus:ring-2 focus:ring-emerald-500 outline-none" placeholder="联系人（选填）" value={contact} onChange={(e) => setContact(e.target.value)} />
      </div>

      {err && <div className="text-red-500 text-sm text-center">{err}</div>}

      <div className="grid gap-3">
        {plans.map((p: any) => (
          <div key={p.id} className="bg-white rounded-2xl shadow p-4 flex items-center justify-between">
            <div>
              <div className="font-bold">{p.name}</div>
              <div className="text-xs text-slate-400 mt-1">
                {p.valid_days ? `授权 ${p.valid_days} 天` : '永久授权'}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-emerald-700">¥{p.price}</div>
              <button
                disabled={busy}
                onClick={(e) => submit(e, p)}
                className="mt-2 bg-emerald-600 text-white rounded-lg px-4 py-2 text-sm font-bold hover:bg-emerald-700 active:scale-95 transition disabled:opacity-60"
              >
                {busy ? '处理中…' : p.price > 0 ? '购买' : '免费领取'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {!wxpayEnabled && (
        <div className="text-xs text-slate-400 text-center">
          注：微信支付尚未配置，提交订单后由后台确认收款自动开通。
        </div>
      )}
    </div>
  );
}
